import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import type { ReactNode } from "react";
import { cn } from "../cn";

// Tabs of a detail (design-system.md §4.3: the history in a tab): arrow keys move between tabs, the panel follows.

interface TabsProps<V extends string> {
  value: V;
  onValueChange: (value: V) => void;
  tabs: { value: V; label: ReactNode }[];
  label: string;
  className?: string;
  children: ReactNode;
}

export function Tabs<V extends string>({ value, onValueChange, tabs, label, className, children }: TabsProps<V>) {
  return (
    <BaseTabs.Root
      value={value}
      onValueChange={(next) => onValueChange(next as V)}
      className={cn("flex min-h-0 flex-col gap-3", className)}
    >
      <BaseTabs.List aria-label={label} className="flex shrink-0 gap-1 overflow-x-auto border-b border-border">
        {tabs.map((t) => (
          <BaseTabs.Tab
            key={t.value}
            value={t.value}
            className="-mb-px shrink-0 border-b-2 border-transparent px-3 py-1.5 whitespace-nowrap pointer-coarse:min-h-(--control-h) text-sm text-muted-foreground data-[active]:border-brand data-[active]:text-foreground"
          >
            {t.label}
          </BaseTabs.Tab>
        ))}
      </BaseTabs.List>
      {children}
    </BaseTabs.Root>
  );
}

export function TabPanel({ value, className, children }: { value: string; className?: string; children: ReactNode }) {
  return (
    <BaseTabs.Panel value={value} className={cn("min-h-0 flex-1", className)}>
      {children}
    </BaseTabs.Panel>
  );
}
